import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface FogEffectProps {
  intensity?: number;      // 0-1
  layerCount?: number;
  areaSize?: [number, number]; // width, depth
  color?: string;
  height?: number;
}

export function FogEffect({
  intensity = 0.5,
  layerCount = 8,
  areaSize = [20, 20],
  color = '#9090a0',
  height = 2,
}: FogEffectProps) {
  const groupRef = useRef<THREE.Group>(null);

  // Create fog layers at different heights
  const layers = useMemo(() => {
    const result = [];
    for (let i = 0; i < layerCount; i++) {
      result.push({
        y: 0.2 + (i / layerCount) * height,
        scale: 0.8 + Math.random() * 0.4,
        phase: Math.random() * Math.PI * 2,
        speed: 0.1 + Math.random() * 0.2,
        opacity: (0.1 + Math.random() * 0.1) * intensity,
      });
    }
    return result;
  }, [layerCount, height, intensity]);

  useFrame((state) => {
    if (!groupRef.current) return;

    const time = state.clock.elapsedTime;

    groupRef.current.children.forEach((child, i) => {
      const layer = layers[i];
      if (layer && child instanceof THREE.Mesh) {
        // Gentle drifting motion
        child.position.x = Math.sin(time * layer.speed + layer.phase) * 2;
        child.position.z = Math.cos(time * layer.speed * 0.7 + layer.phase) * 2;

        // Slight opacity pulsing
        const material = child.material as THREE.MeshBasicMaterial;
        material.opacity = layer.opacity * (0.8 + Math.sin(time * 0.5 + layer.phase) * 0.2);
      }
    });
  });

  if (intensity === 0) return null;

  return (
    <group ref={groupRef}>
      {layers.map((layer, i) => (
        <mesh
          key={i}
          position={[0, layer.y, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={[layer.scale, layer.scale, 1]}
        >
          <planeGeometry args={[areaSize[0] * 1.5, areaSize[1] * 1.5]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={layer.opacity}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}
